import { useState } from 'react';
import { Alert, Box, Button, Checkbox, FormControlLabel, Stack, TextField, Typography } from '@mui/material';
import api from '../../services/api';
import HeroPanelFrame from './HeroPanelFrame';

export default function HeroRegistrationPanel({ onClose }) {
  const [busy, setBusy] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const submit = async (event) => {
    event.preventDefault();
    if (busy) return;
    const fields = new FormData(event.currentTarget);
    setBusy(true);
    setError('');
    try {
      await api.post('/api/web/registro', { ...Object.fromEntries(fields), acepta_contacto: fields.get('acepta_contacto') === 'on' }, { timeout: 45000 });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.error || 'We could not save your registration. Please try again.');
    } finally {
      setBusy(false);
    }
  };
  const field = (name, label, maxLength, extra = {}) => (
    <TextField name={name} label={label} required fullWidth size="small"
      disabled={busy} inputProps={{ maxLength }} {...extra} />
  );
  return (
    <HeroPanelFrame titleId="hero-registration-title" closeLabel="Close registration" onClose={onClose} scrollable={false} sx={{ pb: 3 }}>
      <Box lang="en">
      <Typography component="h2" id="hero-registration-title" sx={{ fontFamily: 'Georgia, serif', fontSize: { xs: 34, lg: 52 }, lineHeight: 1.12 }}>Customer registration</Typography>
      {success ? <Box role="status" sx={{ mt: 2 }}>
        <Typography sx={{ fontWeight: 700, fontSize: 20, mb: 1 }}>Thank you for registering!</Typography>
        <Typography>We have received your company details. Our team will contact you soon.</Typography>
      </Box> : <>
        <Typography sx={{ mt: 0.5, mb: 2, fontSize: 14, lineHeight: 1.4 }}>Tell us about your company. Let's grow together.</Typography>
        <Stack component="form" onSubmit={submit} spacing={1.5} aria-busy={busy} sx={{
          '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: 'rgba(255,255,255,.28)' },
          '& input.MuiInputBase-input': { height: 'auto', maxWidth: 'none' },
          '& textarea.MuiInputBase-input': { minHeight: 0, maxWidth: 'none', resize: 'none' },
          '& .MuiInputLabel-root': { color: '#30443b' },
          '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#795b29' }
        }}>
          {error && <Alert severity="error">{error}</Alert>}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
            {field('empresa', 'Company name', 160, { autoComplete: 'organization' })}
            {field('representante_legal', 'Legal Representative', 160)}
          </Stack>
          {field('direccion_empresa', 'Company’s Address', 500, { autoComplete: 'street-address', multiline: true, rows: 2 })}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
            {field('telefonos', 'Phones', 160, { type: 'tel', autoComplete: 'tel', helperText: 'Include country codes. Separate numbers with commas.' })}
            {field('correo', 'E-mail', 254, { type: 'email', autoComplete: 'email' })}
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
            {field('representante_compras', 'Buying representative', 160)}
            {field('representante_pagos', 'Payment representative', 160)}
          </Stack>
          <Box aria-hidden="true" sx={{ display: 'none' }}><input name="website" tabIndex={-1} autoComplete="off" /></Box>
          <FormControlLabel control={<Checkbox name="acepta_contacto" required disabled={busy} sx={{ color: '#183f2a' }} />} label={<Typography sx={{ fontSize: 12, color: '#40554b' }}>I authorize Fresh Exports to store these details and contact me about its products.</Typography>} />
          <Button type="submit" variant="contained" disabled={busy} sx={{ alignSelf: 'flex-start', bgcolor: '#183f2a', borderRadius: 999, px: 3, py: 1, textTransform: 'none', fontWeight: 800, '&:hover': { bgcolor: '#102d1d' } }}>{busy ? 'Submitting…' : 'Register company ↗'}</Button>
          <Typography sx={{ fontSize: 12, color: '#40554b' }}>No account or password required.</Typography>
        </Stack>
      </>}
      </Box>
    </HeroPanelFrame>
  );
}
